'use client';

import type { Table } from '@tanstack/react-table';
import { Button } from '@zealot/ui/components/button';
import { Input } from '@zealot/ui/components/input';
import { useDebouncedCallback } from '@zealot/ui/hooks/useDebounce';
import { cn } from '@zealot/ui/lib/utils';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type DataTableToolbarProps<TData> = {
  table: Table<TData>;
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  filterPlaceholder?: string;
  filterDebounceMs?: number;
  children?: React.ReactNode;
  className?: string;
};

// gloss
const DataTableToolbar = <TData,>({
  globalFilter,
  onGlobalFilterChange,
  filterPlaceholder = 'Search...',
  filterDebounceMs,
  children,
  className,
}: DataTableToolbarProps<TData>) => {
  const [inputValue, setInputValue] = useState(globalFilter);
  const lastPropagatedRef = useRef(globalFilter);
  const isDebounced = filterDebounceMs !== undefined && filterDebounceMs > 0;

  const propagate = (value: string) => {
    lastPropagatedRef.current = value;
    onGlobalFilterChange(value);
  };
  const propagateDebounced = useDebouncedCallback(propagate, filterDebounceMs);

  useEffect(() => {
    if (globalFilter !== lastPropagatedRef.current) {
      propagateDebounced.cancel();
      setInputValue(globalFilter);
    }
    lastPropagatedRef.current = globalFilter;
  }, [globalFilter, propagateDebounced]);

  const handleChange = (value: string) => {
    setInputValue(value);
    if (!isDebounced) {
      propagate(value);
    } else if (value === globalFilter) {
      propagateDebounced.cancel();
    } else {
      propagateDebounced(value);
    }
  };

  const handleReset = () => {
    propagateDebounced.cancel();
    setInputValue('');
    propagate('');
  };

  const isFiltered = inputValue.length > 0;

  return (
    <div data-slot="data-table-toolbar" className={cn('flex items-center justify-between gap-2 py-3', className)}>
      <div className="flex flex-1 items-center gap-2">
        <Input
          startIcon={<Search />}
          placeholder={filterPlaceholder}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          size="lg"
          className="max-w-sm"
        />
        {isFiltered && (
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Reset
            <X data-icon="inline-end" />
          </Button>
        )}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
};

export type { DataTableToolbarProps };
export { DataTableToolbar };
